import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { CrossFade } from '../../../base/CrossFade';
import {
  LOADING_GRACE_MS,
  LOADING_MIN_DISPLAY_MS,
} from '../../../../hooks/useSmartLoading';
import { useDetailsViewContext } from '../DetailsViewContext';

import {
  ResourceHeader,
  ResourceImage,
  ProductCard,
  ResourceCard,
  ContactCard,
  CalendarSection,
} from '../components';
import { ProductCardSkeleton } from '../components/ProductCard';
import { ResourceCardSkeleton } from '../components/ResourceCard';
import { ContactCardSkeleton } from '../components/ContactCard';
import { ResourceImageSkeleton } from '../components/ResourceImage';
import { ResourceHeaderSkeleton } from '../components/ResourceHeader';
import useCardHeightDistribution from '../hooks/useCardHeightDistribution';

/**
 * Desktop layout - side-by-side arrangement
 * Left: Header -> Image -> Product -> Resource -> Contact
 * Right: Calendar
 *
 * Uses dynamic height distribution for ProductCard and ResourceCard
 * based on their content length.
 */
export default function DesktopLayout() {
  const { syncedLoading, showContactData, publicData, product, height } =
    useDetailsViewContext();

  const hasImage = Boolean(publicData?.resource?.imgUrl);
  const hasResourceCard = Boolean(
    publicData?.resource?.description || publicData?.resource?.url,
  );
  const hasContactData = Boolean(
    publicData?.contact?.name ||
      publicData?.contact?.email ||
      publicData?.contact?.phone ||
      publicData?.contact?.mobile,
  );
  const hasContact = showContactData && hasContactData;

  const {
    headerRef,
    imageRef,
    contactRef,
    registerProductCard,
    registerResourceCard,
    productCardHeight,
    resourceCardHeight,
    productAutoExpand,
    resourceAutoExpand,
  } = useCardHeightDistribution({
    totalHeight: height,
    product,
    resource: publicData?.resource,
    hasImage,
    hasContact,
    hasResourceCard,
  });

  return (
    <Grid id="desktop-layout" container columnSpacing={2} rowSpacing={0}>
      {/* Left Panel - Details */}
      <Grid
        xs={5}
        container
        item
        sx={{
          alignContent: 'flex-start',
          overflowY: 'auto',
          maxHeight: `${height}px`,
        }}
      >
        <CrossFade
          showSkeleton={syncedLoading}
          graceMs={LOADING_GRACE_MS}
          minDisplayMs={LOADING_MIN_DISPLAY_MS}
          skeleton={<LoadingSkeleton />}
        >
          <Grid container rowSpacing={1}>
            {/* Resource Image */}
            {hasImage && (
              <Grid item xs={12}>
                <Box ref={imageRef}>
                  <ResourceImage />
                </Box>
              </Grid>
            )}

            {/* Resource Header */}
            <Grid item xs={12}>
              <Box ref={headerRef}>
                <ResourceHeader />
              </Box>
            </Grid>

            {/* Resource Description */}
            {hasResourceCard && (
              <Grid item xs={12}>
                <ResourceCard
                  allocatedHeight={resourceCardHeight}
                  autoExpand={resourceAutoExpand}
                  onDimensionsChange={registerResourceCard}
                />
              </Grid>
            )}

            {/* Product Card */}
            <Grid item xs={12}>
              <ProductCard
                allocatedHeight={productCardHeight}
                autoExpand={productAutoExpand}
                onDimensionsChange={registerProductCard}
              />
            </Grid>
            {/* Contact Information */}
            {hasContact && (
              <Grid item xs={12}>
                <Box ref={contactRef}>
                  <ContactCard contact={publicData.contact} />
                </Box>
              </Grid>
            )}
          </Grid>
        </CrossFade>
      </Grid>
      {/* Right Panel - Calendar */}
      <Grid container item xs={7}>
        <CalendarSection />
      </Grid>
    </Grid>
  );
}

/**
 * Loading skeleton
 */
function LoadingSkeleton() {
  return (
    <Grid container rowSpacing={1}>
      {/* Image skeleton */}
      <Grid item xs={12}>
        <ResourceImageSkeleton />
      </Grid>

      {/* Header skeleton */}
      <Grid item xs={12}>
        <ResourceHeaderSkeleton />
      </Grid>

      {/* Resource description skeleton */}
      <Grid item xs={12}>
        <ResourceCardSkeleton />
      </Grid>

      {/* Product card skeleton */}
      <Grid item xs={12}>
        <ProductCardSkeleton />
      </Grid>

      {/* Contact skeleton */}
      <Grid item xs={12}>
        <ContactCardSkeleton />
      </Grid>
    </Grid>
  );
}
